// src/utils/businessDay.js
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';

// Cache for business settings to avoid repeated Firestore calls
let cachedBusinessSettings = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getBusinessSettings = async () => {
  const now = Date.now();
  
  // Return cached settings if still valid
  if (cachedBusinessSettings && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
    return cachedBusinessSettings;
  }
  
  try {
    // Tenant-scoped business settings
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      console.warn('getBusinessSettings: tenantId not resolved, returning defaults');
      cachedBusinessSettings = { endTime: '24:00' };
      return cachedBusinessSettings;
    }

    const docRef = doc(db, `tenants/${tenantId}/businessSettings`, 'current');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      cachedBusinessSettings = docSnap.data();
    } else {
      // Default settings if none exist
      cachedBusinessSettings = { endTime: '24:00' };
    }
    
    cacheTimestamp = now;
    return cachedBusinessSettings;
  } catch (error) {
    console.error('Error fetching business settings:', error);
    // Return default on error
    return { endTime: '24:00' };
  }
};

// Clear cache when settings are updated
export const clearBusinessSettingsCache = () => {
  cachedBusinessSettings = null;
  cacheTimestamp = null;
};

// Synchronously return the cached business settings if available.
// This lets UI components read the last-known settings immediately
// during initial render without awaiting the network call.
export const getCachedBusinessSettings = () => cachedBusinessSettings || null;

// Convert business end time to minutes since midnight
export const getBusinessEndMinutes = (endTime) => {
  // Accept either:
  // - a time string like '23:30' or '24:00'
  // - an object like { hour: 1, minute: 15 }
  if (!endTime) return 24 * 60; // default midnight
  if (typeof endTime === 'object') {
    const hour = Number(endTime.hour);
    const minutes = Number(endTime.minute);
    if (Number.isNaN(hour) || Number.isNaN(minutes)) return 24 * 60;
    if (hour === 24 && minutes === 0) return 24 * 60;
    if (hour < 0 || hour > 23 || minutes < 0 || minutes > 59) return 24 * 60;
    return hour * 60 + minutes;
  }
  if (typeof endTime !== 'string') return 24 * 60;
  if (endTime === '24:00') return 24 * 60;

  const parts = endTime.split(':');
  if (!parts || parts.length < 1) return 24 * 60;
  const hours = Number(parts[0] || 0);
  const minutes = Number(parts[1] || 0);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 24 * 60;
  return hours * 60 + minutes;
};

// Get the business date for a given datetime
export const getBusinessDate = async (datetime) => {
  const settings = await getBusinessSettings();
  const endMinutes = getBusinessEndMinutes(settings.endTime);
  
  const date = new Date(datetime);
  const currentMinutes = date.getHours() * 60 + date.getMinutes();
  
  // If we're before the business end time, it's still the previous business day
  if (currentMinutes < endMinutes && endMinutes < 24 * 60) {
    const businessDate = new Date(date);
    businessDate.setDate(businessDate.getDate() - 1);
    return businessDate.toISOString().split('T')[0];
  }
  
  return date.toISOString().split('T')[0];
};

// Check if a time span crosses the business day boundary
export const isOvernightShift = async (startTime, endTime) => {
  // Business settings aren't needed for this calculation right now
  
  // Convert times to minutes
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  
  // Standard case: end time is after start time on same day
  if (endMinutes > startMinutes) {
    return false;
  }
  
  // Potential overnight shift: end time is before start time
  // This is valid if it crosses the business day boundary
  return startMinutes >= endMinutes;
};

// Validate if a shift time is within business hours
// validateShiftTime(startTime, endTime, options)
// options.endTimeOverride: optional string or {hour, minute} override from location settings
export const validateShiftTime = async (startTime, endTime, options = {}) => {
  try {
    // Use override if provided (e.g. from location.businessDayCutoff)
    let endTimeValue = null;
    if (options && options.endTimeOverride) {
      endTimeValue = options.endTimeOverride;
    }
    const settings = await getBusinessSettings();
  // Reserved for future business-hours validations
  // eslint-disable-next-line no-unused-vars
  const businessEndMinutes = getBusinessEndMinutes(endTimeValue || settings.endTime);
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    let shiftEndMinutes = endHour * 60 + endMin;
    
    // Handle overnight shifts
    if (shiftEndMinutes <= startMinutes) {
      shiftEndMinutes += 24 * 60; // Add 24 hours for next day
    }
    
    // Calculate shift duration
    const shiftDuration = shiftEndMinutes - startMinutes;
    
    // Validate shift duration (max 24 hours)
    if (shiftDuration > 24 * 60) {
      return {
        valid: false,
        error: 'Shift cannot be longer than 24 hours'
      };
    }
    
    // Minimum shift duration (15 minutes)
    if (shiftDuration < 15) {
      return {
        valid: false,
        error: 'Shift must be at least 15 minutes long'
      };
    }
    
    return {
      valid: true,
      isOvernight: shiftEndMinutes > 24 * 60 + startMinutes,
      duration: shiftDuration
    };
    
  } catch (error) {
    console.error('Error validating shift time:', error);
    return {
      valid: false,
      error: 'Unable to validate shift time'
    };
  }
};

// Get business day range for reporting
export const getBusinessDayRange = async (businessDate) => {
  const settings = await getBusinessSettings();
  const endMinutes = getBusinessEndMinutes(settings.endTime);
  
  const startDate = new Date(businessDate);
  const endDate = new Date(businessDate);
  
  if (endMinutes < 24 * 60) {
    // Business day ends after midnight of the next calendar day
    endDate.setDate(endDate.getDate() + 1);
    endDate.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);
    startDate.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 1); // Start 1 minute after previous end
  } else {
    // Standard calendar day
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
  }
  
  return { startDate, endDate };
};
